import axios from 'axios';
import { secureStorage } from '../utilities/securityUtils';

const BASE_URL_USER_SERVICE = process.env.REACT_APP_API_URL_USER_SERVICE;

const pendingRequestControllers = new Set();
const requestControllerKey = '__requestAbortController';

const isCanceledError = error =>
  axios.isCancel(error) ||
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError';

const createCanceledResponse = config => ({
  data: undefined,
  status: 0,
  statusText: 'canceled',
  headers: {},
  config,
  request: null,
  aborted: true,
});

const registerRequestController = config => {
  if (config?.signal || config?.skipRouteAbort) {
    return config;
  }

  const controller = new AbortController();
  pendingRequestControllers.add(controller);

  return {
    ...config,
    signal: controller.signal,
    [requestControllerKey]: controller,
  };
};

const unregisterRequestController = config => {
  const controller = config?.[requestControllerKey];
  if (controller) {
    pendingRequestControllers.delete(controller);
  }
};

const abortPendingRequests = () => {
  pendingRequestControllers.forEach(controller => controller.abort());
  pendingRequestControllers.clear();
};

function createSecureAxiosInstance(baseURL) {
  const instance = axios.create({
    baseURL,
    // timeout: 10000,
  });

  instance.interceptors.request.use(
    config => {
      const token = secureStorage.getItem('token');
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Validate request origin
      if (!validateRequestOrigin(config.url, baseURL)) {
        throw new Error('Security violation: Invalid request origin');
      }

      return registerRequestController(config);
    },
    error => Promise.reject(error)
  );

  instance.interceptors.response.use(
    response => {
      unregisterRequestController(response.config);
      return response;
    },
    error => {
      unregisterRequestController(error?.config);
      if (isCanceledError(error)) {
        return createCanceledResponse(error?.config);
      }

      return Promise.reject(error);
    }
  );

  return instance;
}

function validateRequestOrigin(url, allowedBaseURL) {
  if (url && url.startsWith('http')) {
    try {
      const requestOrigin = new URL(url).origin;
      const baseOrigin = new URL(allowedBaseURL).origin;
      return requestOrigin === baseOrigin;
    } catch {
      return false;
    }
  }
  return true;
}

function validateRequestOriginFullUrl(fullUrl) {
  if (typeof window !== 'undefined' && window.Cypress) {
    return true;
  }
  // Optional: validate domain or protocol as needed
  try {
    const url = new URL(fullUrl);
    return ['https:', 'http:'].includes(url.protocol); // Customize this check
  } catch {
    return false;
  }
}

const secureAxiosUserService = createSecureAxiosInstance(BASE_URL_USER_SERVICE);
const secureAxios = axios.create();

secureAxios.interceptors.request.use(
  config => {
    const token = secureStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.url && !validateRequestOriginFullUrl(config.url)) {
      throw new Error('Security violation: Invalid request origin');
    }

    return registerRequestController(config);
  },
  error => Promise.reject(error)
);

secureAxios.interceptors.response.use(
  response => {
    unregisterRequestController(response.config);
    return response;
  },
  error => {
    unregisterRequestController(error?.config);
    if (
      error?.response &&
      error?.response?.status === 401 &&
      error?.response?.data?.message === 'Token has expired'
    ) {
      window.dispatchEvent(new CustomEvent('auth:refresh'));
    }
    if (isCanceledError(error)) {
      return createCanceledResponse(error?.config);
    }

    return Promise.reject(error);
  }
);

const getAccessToken = () => secureStorage.getItem('token');
const getRefreshToken = () => secureStorage.getItem('refresh-token');
const getApiUrl = key => {
  const apiLinks = secureStorage.getItem('api-links');
  const parsedApiLinks = apiLinks ? JSON.parse(apiLinks) : {};
  return parsedApiLinks?.[key] || '';
};

const API = {
  getLoginFeature: formData => {
    const url = `${BASE_URL_USER_SERVICE}/check_white-listed_domains`;
    return secureAxiosUserService.post(url, formData);
  },
  login: urlEncodedData => {
    const url = `${BASE_URL_USER_SERVICE}/login`;
    return secureAxiosUserService.post(url, urlEncodedData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
  loginMFA: formData => {
    const url = `${BASE_URL_USER_SERVICE}/v2/login-mfa`;
    return secureAxiosUserService.post(url, formData);
  },
  validateMFAOTP: formData => {
    const url = `${BASE_URL_USER_SERVICE}/v2/validate-otp`;
    return secureAxiosUserService.post(url, formData);
  },
  refresh: (formData, config = {}) => {
    const url = `${BASE_URL_USER_SERVICE}/refresh`;
    return secureAxiosUserService.post(url, formData, config);
  },
  logout: () => {
    const url = `${BASE_URL_USER_SERVICE}/logout`;
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    if (!accessToken || !refreshToken) {
      return Promise.resolve({ data: { success: true } });
    }

    const payload = {
      acs: `Bearer ${accessToken}`,
      refreshtoken: `Bearer ${refreshToken}`,
    };
    return secureAxios.post(
      url,
      {},
      {
        headers: payload,
      }
    );
  },
  completeRegistration: urlEncodedData => {
    const url = `${BASE_URL_USER_SERVICE}/create-new-password`;
    return secureAxiosUserService.post(url, urlEncodedData.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
  sendOTP: formData => {
    const url = `${BASE_URL_USER_SERVICE}/forgot-password-keycloak`;
    return secureAxiosUserService.post(url, formData);
  },
  resetPassword: formData => {
    const url = `${BASE_URL_USER_SERVICE}/reset-password`;
    return secureAxiosUserService.post(url, formData);
  },
  changePassword: payload => {
    const url = `${getApiUrl('change_password')}`;
    return secureAxiosUserService.post(url, payload);
  },
  getApiLinks: () => {
    const url = `${BASE_URL_USER_SERVICE}/links`;
    return secureAxiosUserService.get(url);
  },
  getTripDetailsFromDeviceIdAndTripIds: payload => {
    const url = `${getApiUrl('trip_detail')}`;
    return secureAxios.post(url, payload);
  },
  getSOSAndRashDrivingDetailsFromDeviceIdAndTripIds: payload => {
    const url = `${getApiUrl('anomaly_gps_data')}`;
    return secureAxios.post(url, payload);
  },
  getGPSDataFromDeviceIdAndTripIds: (payload, page) => {
    const url = `${getApiUrl('gps_data')}?page_size=500&page=${page}`;
    // const url = `https://stage-api-dashboard.ridebuddy.in/gps/data?page_size=500&page=${page}`;
    return secureAxios.post(url, payload);
  },
  getTripReports: (page, pageSize, payload, is_calendar_date) => {
    const url = `${getApiUrl('trip_reports')}?page=${page}&page_size=${pageSize}&is_calendar_date=${is_calendar_date}`;
    return secureAxios.post(url, payload);
  },
  getSosReports: (page, page_size, payload) => {
    const url = `${getApiUrl('sos_logs')}?page=${page}&page_size=${page_size}`;
    return secureAxios.post(url, payload);
  },
  updateSOActionData: payload => {
    const url = `${getApiUrl('sos_form')}`;
    return secureAxios.put(url, payload);
  },
  getOngoingTripDetails: tripID => {
    const url = `${getApiUrl('ongoing_trip_info')}/${tripID}`;
    return secureAxios.get(url);
  },
  getVehicleDetailsFromTrip: payload => {
    const url = `${getApiUrl('vehicle_info')}`;
    return secureAxios.post(url, payload);
  },
  postBroadcastMessage: payload => {
    const url = `${getApiUrl('mqtt_write_broadcast_publish')}`;
    return secureAxios.post(url, payload);
  },
  getBroadcastDayLog: currentDate => {
    const url = `${getApiUrl('broadcast_message_log')}${currentDate}?format=json`;
    return secureAxios.get(url);
  },
  getBroadcastLogs: (page, page_size, payload) => {
    const filter = encodeURIComponent(JSON.stringify(payload?.filter || {}));
    const sort = encodeURIComponent(JSON.stringify(payload?.sort || {}));
    const url = `${getApiUrl('broadcast_reports')}?filter=${filter}&from_date=${payload?.start_date}&page=${page}&page_size=${page_size}&sort=${sort}&to_date=${payload?.end_date}`;
    return secureAxios.get(url);
  },
  getBroadcastLogsDownload: (page, page_size, payload, config = {}) => {
    const filter = encodeURIComponent(JSON.stringify(payload?.filter || {}));
    const url = `${getApiUrl('broadcast_reports')}?page=${page}&page_size=${page_size}
    &filter=${filter}&from_date=${payload?.start_date}&to_date=${payload?.end_date}&download=1`;
    return secureAxios.get(url, config);
  },
  getSOSColumnData: is_calendar_date => {
    const url = `${getApiUrl('sos_status_list')}?is_calendar_date=${is_calendar_date}`;
    return secureAxios.get(url);
  },
  getAnomalyColumnData: (is_calendar_date, date) => {
    let url = `${getApiUrl('anomaly_groups_details')}?is_calendar_date=${is_calendar_date}`;
    if (is_calendar_date && date) {
      url += `&date=${date}`;
    }
    return secureAxios.get(url);
  },
  getSOSLogsDownload: (page, page_size, payload, config = {}) => {
    const url = `${getApiUrl('sos_logs_download')}?page=${page}&page_size=${page_size}`;
    return secureAxios.post(url, payload, config);
  },
  getAnomalyReports: (page, page_size, payload) => {
    const url = `${getApiUrl('safety_anomaly_logs')}?page=${page}&page_size=${page_size}`;
    return secureAxios.post(url, payload);
  },
  getAnomalyLogsDownload: (page, page_size, payload, config = {}) => {
    const url = `${getApiUrl('safety_anomaly_logs_download')}?page=${page}&page_size=${page_size}`;
    return secureAxios.post(url, payload, config);
  },
  getDriverDrowsinessLogs: (page, page_size, payload) => {
    const url = `${getApiUrl('dms_logs')}?page=${page}&page_size=${page_size}`;
    return secureAxios.post(url, payload);
  },
  getDriverDrowsinessLogsDownload: (page, page_size, payload, config = {}) => {
    const url = `${getApiUrl('dms_logs_download')}?page=${page}&page_size=${page_size}`;
    return secureAxios.post(url, payload, config);
  },
  getUniqueVehicleDetails: (page, page_size) => {
    const url = `${getApiUrl('device_coupling')}?page=${page}&page_size=${page_size}`;
    return secureAxios.get(url);
  },
  getTripReportsWithDateFilter: (
    page,
    page_size,
    filter,
    sort,
    vehicleNumber,
    tripIds,
    dateRange,
    download,
    config = {}
  ) => {
    const additionalKeys = vehicleNumber
      ? `&vehicle_numbers=${vehicleNumber}`
      : `&trip_ids=${tripIds}`;
    const url = `${getApiUrl('trip_history')}?page=${page}&page_size=${page_size}&filter=${filter}
    &sort=${sort}&start_date=${dateRange[0]}&end_date=${dateRange[1]}&download=${download}${additionalKeys}`;
    return secureAxios.get(url, config);
  },
  getTripReportsSummary: payload => {
    const url = `${getApiUrl('trip-end-summary')}`;
    return secureAxios.post(url, payload);
  },
  requestDmsFilesAccess: payload => {
    const url = `${getApiUrl('dms_video_request')}`;
    return secureAxios.post(url, payload);
  },
  getDmsVideoFile: payload => {
    const url = `${getApiUrl('dms_video')}`;
    return secureAxios.post(url, payload);
  },
  getSOSReportsWithDateFilter: (
    page,
    page_size,
    filter,
    sort,
    vehicleNumber,
    tripIds,
    dateRange,
    download,
    config = {}
  ) => {
    const additionalKeys = vehicleNumber
      ? `&vehicle_numbers=${vehicleNumber}`
      : `&trip_ids=${tripIds}`;
    const url = `${getApiUrl('sos_history')}?page=${page}&page_size=${page_size}&filter=${filter}
    &sort=${sort}&start_date=${dateRange[0]}&end_date=${dateRange[1]}&download=${download}${additionalKeys}`;
    return secureAxios.get(url, config);
  },
  getSOSReportsSummary: payload => {
    const url = `${getApiUrl('sos_summary')}`;
    return secureAxios.post(url, payload);
  },
  getAnomalyReportsWithDateFilter: (
    page,
    page_size,
    filter,
    sort,
    vehicleNumber,
    tripIds,
    dateRange,
    download,
    config = {}
  ) => {
    const additionalKeys = vehicleNumber
      ? `&vehicle_numbers=${vehicleNumber}`
      : `&trip_ids=${tripIds}`;
    const url = `${getApiUrl('anomaly_history')}?page=${page}&page_size=${page_size}&filter=${filter}
    &sort=${sort}&start_date=${dateRange[0]}&end_date=${dateRange[1]}&download=${download}${additionalKeys}`;
    return secureAxios.get(url, config);
  },
  getAnomalyReportsSummary: payload => {
    const url = `${getApiUrl('anomaly_summary')}`;
    return secureAxios.post(url, payload);
  },
  getDriverDrowsinessReportsWithDateFilter: (
    page,
    page_size,
    filter,
    sort,
    vehicleNumber,
    tripIds,
    dateRange,
    download,
    config = {}
  ) => {
    const additionalKeys = vehicleNumber
      ? `&vehicle_numbers=${vehicleNumber}`
      : `&trip_ids=${tripIds}`;
    const url = `${getApiUrl('dms_reports')}?page=${page}&page_size=${page_size}&filter=${filter}
    &sort=${sort}&start_date=${dateRange[0]}&end_date=${dateRange[1]}&download=${download}${additionalKeys}`;
    return secureAxios.get(url, config);
  },
  getDriverDrowsinessReportsSummary: payload => {
    const url = `${getApiUrl('dms_logs_summary')}`;
    return secureAxios.post(url, payload);
  },
  createSoTicket: payload => {
    const url = `${getApiUrl('sos_ticket')}`;
    return secureAxios.post(url, payload);
  },
  getDriverDrowsinessData: (payload, page, pageSize) => {
    const url = `${getApiUrl('dms_events')}?page_size=${pageSize}&page=${page}`;
    return secureAxios.post(url, payload);
  },
  getTripDriverDrowsinessMapData: (tripId, true_dms_drowsy, ticketStatus) => {
    const url = `${getApiUrl('dms_map_mode')}?trip_id=${tripId}&true_dms_drowsy=${true_dms_drowsy}&ticket_status=${ticketStatus}`;
    return secureAxios.get(url);
  },
  getTripDriverDrowsinessDetails: (tripId, true_dms_drowsy, ticketStatus) => {
    const url = `${getApiUrl('dms_events')}?trip_id=${tripId}&true_dms_drowsy=${true_dms_drowsy}&ticket_status=${ticketStatus}`;
    return secureAxios.get(url);
  },
  postDriverDrowsinessAction: payload => {
    const url = `${getApiUrl('dms_action')}`;
    return secureAxios.post(url, payload);
  },
  postDriverDrowsinessViewed: payload => {
    const url = `${getApiUrl('dms_viewed_status')}`;
    return secureAxios.post(url, payload);
  },
  getAnomalyAlertsChartData: (is_calendar_date, true_dms_drowsy) => {
    const url = `${getApiUrl('anomaly_section_data')}?is_calendar_date=${is_calendar_date}&true_dms_drowsy=${true_dms_drowsy}`;
    return secureAxios.get(url);
  },
  getVehicleStatusChartData: is_calendar_date => {
    const url = `${getApiUrl('device_status_count')}?is_calendar_date=${is_calendar_date}`;
    return secureAxios.get(url);
  },
  getVehicleOverviewData: () => {
    const url = `${getApiUrl('active_trips_live_gps')}`;
    return secureAxios.get(url);
  },
  getCompaniesList: (config = {}) => {
    const url = `${getApiUrl('list_org')}`;
    return secureAxios.get(url, config);
  },
  addOrgDetails: payload => {
    const url = `${getApiUrl('org_onboard')}`;
    return secureAxios.post(url, payload);
  },
  updateOrgDetails: (orgId, payload) => {
    const url = `${getApiUrl('edit_org')}/${orgId}`;
    return secureAxios.patch(url, payload);
  },
  deleteOrg: payload => {
    const url = `${getApiUrl('delete_org')}`;
    return secureAxios.delete(url, { data: payload });
  },
  addCompanyAdmin: payload => {
    const url = `${getApiUrl('admin_onboard')}`;
    return secureAxios.post(url, payload);
  },
  getCompanyUsersList: () => {
    const url = `${getApiUrl('list_users')}`;
    return secureAxios.get(url);
  },
  addCompanyLocation: payload => {
    const url = `${getApiUrl('loc_onboard')}`;
    return secureAxios.post(url, payload);
  },
  deleteBranchLocation: payload => {
    const url = `${getApiUrl('delete_loc')}`;
    return secureAxios.delete(url, { data: payload });
  },
  getPermissionsList: () => {
    const url = `${getApiUrl('list_permissions')}`;
    return secureAxios.get(url);
  },
  inviteCompanyUser: payload => {
    const url = `${getApiUrl('transport_ops_onboard')}`;
    return secureAxios.post(url, payload);
  },
  reInviteUsers: payload => {
    const url = `${getApiUrl('re_invite_new_user')}?email=${payload?.email}`;
    return secureAxios.get(url);
  },
  getRoles: () => {
    const url = `${getApiUrl('list_roles')}`;
    return secureAxios.get(url);
  },
  editUserRole: payload => {
    const url = `${getApiUrl('edit_role_user')}`;
    return secureAxios.put(url, payload);
  },
  deleteUser: payload => {
    const url = `${getApiUrl('delete_user')}`;
    return secureAxios.delete(url, { data: payload });
  },
  editUserLocationPermission: payload => {
    const url = `${getApiUrl('edit_loc_user')}`;
    return secureAxios.put(url, payload);
  },
  editCompanyBranchAddress: payload => {
    const url = `${getApiUrl('edit_branch_address')}`;
    return secureAxios.patch(url, payload);
  },
  getUserActivityLogs: (page, page_size, payload) => {
    const url = `${getApiUrl('user_activity_log')}?page=${page}&page_size=${page_size}`;
    return secureAxios.post(url, payload);
  },
  getDmsErrorLogs: (page, page_size, payload) => {
    const url = `${getApiUrl('camera_error_logs')}?page=${page}&page_size=${page_size}
    &filters=${encodeURIComponent(JSON.stringify(payload?.filter))}
    &sort=${encodeURIComponent(JSON.stringify(payload?.sort))}
    &start_date=${encodeURIComponent(payload?.start_date)}
    &end_date=${encodeURIComponent(payload?.end_date)}`;
    return secureAxios.get(url);
  },
  getDmsErrorLogsDownload: (page, page_size, payload, config = {}) => {
    const url = `${getApiUrl('camera_error_logs_download')}?page=${page}&page_size=${page_size}
    &filters=${encodeURIComponent(JSON.stringify(payload?.filter))}
    &sort=${encodeURIComponent(JSON.stringify(payload?.sort))}
    &start_date=${encodeURIComponent(payload?.start_date)}
    &end_date=${encodeURIComponent(payload?.end_date)}`;
    return secureAxios.get(url, config);
  },
  postDmsErrorResolved: payload => {
    const url = `${getApiUrl('dms_viewed_status')}`;
    return secureAxios.post(url, payload);
  },
  getDevicesList: (page, page_size, payload) => {
    const url = `${getApiUrl('list_devices_dropdown')}?page=${page}&page_size=${page_size}`;
    return secureAxios.post(url, payload);
  },
  getDeviceList: (page, page_size, payload) => {
    const url = `${getApiUrl('device_vehicle_org_mappings')}?page=${page}&page_size=${page_size}`;
    return secureAxios.post(url, payload);
  },
  getDevicesActivityLogs: (page, page_size, payload) => {
    const url = `${getApiUrl('device_vehicle_activity_logs')}?page=${page}&page_size=${page_size}`;
    return secureAxios.post(url, payload);
  },
  onboardDevice: payload => {
    const url = `${getApiUrl('device_onboard')}`;
    return secureAxios.post(url, payload);
  },
  installDevice: payload => {
    const url = `${getApiUrl('device_vehicle_commission')}`;
    return secureAxios.post(url, payload);
  },
  replaceDevice: payload => {
    const url = `${getApiUrl('device_vehicle_replacement')}`;
    return secureAxios.post(url, payload);
  },
  uninstallDevice: payload => {
    const url = `${getApiUrl('device_vehicle_decommission')}`;
    return secureAxios.patch(url, payload);
  },
  getVehicleInfo: deviceId => {
    const url = `${getApiUrl('vehicle_details')}/${deviceId}`;
    return secureAxios.get(url);
  },
  getCompaniesSummaryData: () => {
    const url = `${getApiUrl('superadmin_overview_analytics')}`;
    return secureAxios.get(url);
  },
  getOrgRefreshToken: payload => {
    const url = `${getApiUrl('change_org')}`;
    return secureAxios.post(url, payload);
  },
  addPlatformOperationsManager: payload => {
    const url = `${getApiUrl('platform_ops_manager_onboard')}`;
    return secureAxios.post(url, payload);
  },
  getOrganisationFeatures: () => {
    const url = `${getApiUrl('org_features')}`;
    return secureAxios.get(url);
  },
  getTripVideoFragmentDetails: tripId => {
    const url = `${getApiUrl('fetch_fragments')}?trip_id=${tripId}`;
    return secureAxios.get(url);
  },
  postPastTripsVideoAction: payload => {
    const url = `${getApiUrl('vod_comment')}`;
    return secureAxios.post(url, payload);
  },
  postBookmarkTripFragment: payload => {
    const url = `${getApiUrl('save_bookmark')}`;
    return secureAxios.post(url, payload);
  },
  requestTripVideoFragment: payload => {
    const url = `${getApiUrl('vod_request')}`;
    return secureAxios.post(url, payload);
  },
  getVodTripReports: (page, pageSize, payload) => {
    const url = `${getApiUrl('vod_trip_reports')}?page=${page}&page_size=${pageSize}`;
    return secureAxios.post(url, payload);
  },
  getUserDetails: () => {
    const url = `${getApiUrl('user')}`;
    return secureAxios.get(url);
  },
  manageTrip: payload => {
    const url = getApiUrl('mis_trip');
    return secureAxios.post(url, payload);
  },
  getTripsList: payload => {
    const url = `${getApiUrl('trip_id')}`;
    return secureAxios.post(url, payload);
  },
  updateDevicePhoneNumber: payload => {
    const url = `${getApiUrl('device_phone_number')}`;
    return secureAxios.patch(url, payload);
  },
  getAllFeatures: () => {
    const url = `${getApiUrl('display_all_features')}`;
    return secureAxios.get(url);
  },
  getOnboardedDevicesList: (page, page_size, payload) => {
    const url = `${getApiUrl('list_devices')}?page=${page}&page_size=${page_size}`;
    return secureAxios.post(url, payload);
  },
  getCameraErrorReportsWithDateFilter: (
    page,
    page_size,
    filter,
    sort,
    vehicleNumber,
    tripIds,
    dateRange,
    download,
    config = {}
  ) => {
    const additionalKeys = vehicleNumber
      ? `&vehicle_numbers=${vehicleNumber}`
      : `&trip_ids=${tripIds}`;
    const url = `${getApiUrl('camera_anomaly_logs')}?page=${page}&page_size=${page_size}&filter=${filter}
    &sort=${sort}&start_date=${dateRange[0]}&end_date=${dateRange[1]}&download=${download}${additionalKeys}`;
    return secureAxios.get(url, config);
  },
  getCameraErrorReportsSummary: payload => {
    const url = `${getApiUrl('camera_anomaly_summary')}`;
    return secureAxios.post(url, payload);
  },
};

export { API, abortPendingRequests };
